import {
  AppPlatform,
  AppVersionConfig,
  AppVersionFieldErrors,
  AppVersionUpdatePayload,
} from '@/client/types/app-version';

/** Inputs hold strings; integers are converted only when building the payload. */
export type PlatformDraft = Record<string, string>;

// Mirrors DEFAULT_CONFIG in ff-api app_version.py. A field still on its default shows
// empty with the default as placeholder; leaving it empty keeps (or restores) the default.
export const APP_VERSION_DEFAULTS: Record<AppPlatform | 'maintenance', Record<string, string>> = {
  ios: {
    min_version: '1.0.0',
    min_build_number: '14',
    latest_version: '1.1.0',
    latest_build_number: '16',
    store_url: 'https://apps.apple.com/app/idXXXXXXXXX',
    title: 'Update Required',
    message:
      'A new version of FeelyFeely is required to keep enjoying seamless services and security improvements.',
    release_notes: '• Improved performance\n• Bug fixes and stability improvements',
  },
  android: {
    min_version: '1.0.0',
    min_version_code: '14',
    latest_version: '1.1.0',
    latest_version_code: '16',
    store_url: 'market://details?id=com.feelyfeely.mobile',
    fallback_url: 'https://play.google.com/store/apps/details?id=com.feelyfeely.mobile',
    title: 'Update Required',
    message: 'A new version of FeelyFeely is required to continue.',
    release_notes: '• Improved checkout experience\n• Bug fixes',
  },
  maintenance: {
    title: 'Scheduled Maintenance',
    message: "We're currently optimizing FeelyFeely. We'll be back online shortly.",
  },
};

/** Stored value, or '' when it's still the built-in default. */
export function blankIfDefault(
  section: AppPlatform | 'maintenance',
  field: string,
  value: unknown,
): string {
  const text = value == null ? '' : String(value);
  return text === APP_VERSION_DEFAULTS[section][field] ? '' : text;
}

/** What the backend will hold for this field: the typed value, or the default when empty. */
export function effectiveValue(
  section: AppPlatform | 'maintenance',
  field: string,
  value: string,
): string {
  const trimmed = value.trim();
  return trimmed === '' ? (APP_VERSION_DEFAULTS[section][field] ?? '') : trimmed;
}

function effectiveDraft(platform: AppPlatform, draft: PlatformDraft): PlatformDraft {
  return Object.fromEntries(
    Object.entries(draft).map(([field, value]) => [field, effectiveValue(platform, field, value)]),
  );
}

export interface PlatformFieldSpec {
  label: string;
  storeLabel: string;
  minCode: string;
  latestCode: string;
  codeLabel: string;
  codeHint: string;
  textFields: string[];
}

export const PLATFORM_SPECS: Record<AppPlatform, PlatformFieldSpec> = {
  ios: {
    label: 'iOS',
    storeLabel: 'App Store URL',
    minCode: 'min_build_number',
    latestCode: 'latest_build_number',
    codeLabel: 'build number',
    codeHint: 'CFBundleVersion',
    textFields: ['store_url', 'title', 'message', 'release_notes'],
  },
  android: {
    label: 'Android',
    storeLabel: 'Play Store URL (market://)',
    minCode: 'min_version_code',
    latestCode: 'latest_version_code',
    codeLabel: 'version code',
    codeHint: 'versionCode',
    textFields: ['store_url', 'fallback_url', 'title', 'message', 'release_notes'],
  },
};

const VERSION_RE = /^\d+(\.\d+){0,3}$/;
const INT_RE = /^\d+$/;

function platformFields(platform: AppPlatform): string[] {
  const spec = PLATFORM_SPECS[platform];
  return ['min_version', 'latest_version', spec.minCode, spec.latestCode, ...spec.textFields];
}

export function toPlatformDraft(config: AppVersionConfig, platform: AppPlatform): PlatformDraft {
  const block = config[platform] as unknown as Record<string, unknown>;
  return Object.fromEntries(
    platformFields(platform).map((field) => [field, blankIfDefault(platform, field, block[field])]),
  );
}

export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  const size = Math.max(pa.length, pb.length);
  for (let i = 0; i < size; i += 1) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return Math.sign(diff);
  }
  return 0;
}

/** Same rules as the backend's validate_payload, keyed the same way, so errors land in place. */
export function validatePlatformDraft(
  platform: AppPlatform,
  rawDraft: PlatformDraft,
): AppVersionFieldErrors {
  const draft = effectiveDraft(platform, rawDraft);
  const spec = PLATFORM_SPECS[platform];
  const errors: AppVersionFieldErrors = {};
  const key = (field: string) => `${platform}.${field}`;

  for (const field of ['min_version', 'latest_version']) {
    if (!VERSION_RE.test(draft[field].trim())) errors[key(field)] = "Must look like '1.2.3'.";
  }
  for (const field of [spec.minCode, spec.latestCode]) {
    if (!INT_RE.test(draft[field].trim())) errors[key(field)] = 'Must be a whole number ≥ 0.';
  }

  if (
    !errors[key('min_version')] &&
    !errors[key('latest_version')] &&
    compareVersions(draft.min_version.trim(), draft.latest_version.trim()) > 0
  ) {
    errors[key('min_version')] = 'Cannot be greater than the latest version.';
  }
  if (
    !errors[key(spec.minCode)] &&
    !errors[key(spec.latestCode)] &&
    Number(draft[spec.minCode]) > Number(draft[spec.latestCode])
  ) {
    errors[key(spec.minCode)] = `Cannot be greater than the latest ${spec.codeLabel}.`;
  }
  return errors;
}

/** Only changed fields: anything omitted keeps its stored value server-side. */
export function buildPlatformPayload(
  platform: AppPlatform,
  rawSaved: PlatformDraft,
  rawDraft: PlatformDraft,
): AppVersionUpdatePayload {
  const spec = PLATFORM_SPECS[platform];
  const saved = effectiveDraft(platform, rawSaved);
  const draft = effectiveDraft(platform, rawDraft);
  const changes: Record<string, string | number> = {};
  for (const field of platformFields(platform)) {
    if (draft[field] === saved[field]) continue;
    const value = draft[field];
    changes[field] = field === spec.minCode || field === spec.latestCode ? Number(value) : value;
  }
  return { [platform]: changes } as AppVersionUpdatePayload;
}

export function isPlatformDirty(saved: PlatformDraft, draft: PlatformDraft): boolean {
  return Object.keys(saved).some((field) => saved[field] !== draft[field]);
}

/** Plain-language rollout summary for the draft thresholds, or null while they're invalid. */
export function describeRollout(platform: AppPlatform, rawDraft: PlatformDraft): string | null {
  const spec = PLATFORM_SPECS[platform];
  const min = effectiveValue(platform, spec.minCode, rawDraft[spec.minCode]);
  const latest = effectiveValue(platform, spec.latestCode, rawDraft[spec.latestCode]);
  if (!INT_RE.test(min) || !INT_RE.test(latest) || Number(min) > Number(latest)) return null;

  const minN = Number(min);
  const latestN = Number(latest);
  const parts = [
    minN > 0 ? `Builds below ${minN} are blocked until they update.` : 'No build is blocked.',
  ];
  if (latestN > minN) {
    const range = latestN - 1 === minN ? `${minN}` : `${minN}–${latestN - 1}`;
    parts.push(`${range} get a dismissible prompt.`);
  }
  parts.push(`${latestN} and above are up to date.`);
  return parts.join(' ');
}
